import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { isAxiosError } from 'axios';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import './DietaryRecipeDetail.css';
import apiClient from '../services/apiClient';

interface Recipe {
  id: string;
  title: string;
  categories: string[];
  content: string;
}
const extractLeadText = (markdown: string): string => {
  const lines = markdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !/^!\[.*\]\(.*\)$/.test(line) && !line.startsWith('图片:'));
  return lines.find((line) => !line.startsWith('-') && !line.startsWith('*')) ?? '';
};

const DietaryRecipeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 为标签分配独特颜色的函数（与列表页保持一致）
  const getTagColor = (tag: string): string => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
    ];
    const index = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // 根据药膳方标题生成图片文件名
  const getImageFileName = (title: string): string | null => {
    // 将中文标题转换为英文/拼音格式
    const imageMap: { [key: string]: string } = {
      '百合莲子银耳羹': 'bai-he-lian-zi-yin-er-geng.png',
      '陈皮红豆沙': 'chen-pi-hong-dou-sha.png',
      '川贝雪梨中': 'chuan-bei-xue-li-zhong.png',
      '当归生姜羊肉汤': 'dang-gui-ginger-lamb-soup.png',
      '杜仲核桃煮腰汤': 'du-zhong-he-tao-zhu-yao-tang.png',
      '佛手瓜蒌鱼汤': 'fo-shou-gua-ji-yu-tang.png',
      '葛根连藕排骨汤': 'ge-gen-lian-ou-pai-gu-tang.png',
      '枸杞炖鸡': 'goji-chicken-stew.png',
      '桂圆莲子八宝粥': 'gui-yuan-lian-zi-ba-bao-zhou.png',
      '黑豆五鸡汤': 'hei-dou-wu-ji-tang.png',
      '黄芪红枣茶': 'huang-qi-hong-zao-cha.png',
      '金银花甘草茶': 'jin-yin-hua-gan-cao-cha.png',
      '菊花决明子茶': 'ju-hua-jue-ming-zi-cha.png',
      '罗汉果雪梨饮': 'luo-han-guo-xue-li-yin.png',
      '麦冬玉竹老鸭汤': 'mai-dong-yu-zhu-lao-ya-tang.png',
      '玫瑰花茶': 'mei-gui-hua-cha.png',
      '芡实茯苓膏': 'qian-shi-fu-ling-gao.png',
      '三七炖鸡': 'san-qi-chicken-stew.png',
      '桑椹枸杞膏': 'sang-shen-gou-qi-gao.png',
      '山药薏米芡实粥': 'shan-yao-yi-mi-qian-shi-zhou.png',
      '四神汤': 'si-shen-tang.png',
      '天麻炖鱼头': 'tian-ma-dun-yu-tou.png',
      '山药莲子粥': 'yam-lotus-seed-porridge.png'
    };
    return imageMap[title] || null;
  };

  useEffect(() => {
    const fetchRecipeDetail = async () => {
      if (!id) {
        setError('未提供药膳ID');
        setIsLoading(false);
        return;
      }
      try {
        const response = await apiClient.get(`/diet_recipes`, { params: { id } });
        setRecipe(response.data);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          setError('未找到该药膳方');
        } else {
          setError('加载药膳方详情失败');
          console.error(err);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecipeDetail();
  }, [id]);

  if (isLoading) return (
    <div className="unified-page-wrapper">
      <div className="unified-page-header">
        <h3>药膳方详情</h3>
      </div>
      <div className="unified-page-content-scrollable">
        加载中...
      </div>
    </div>
  );
  if (error) return (
    <div className="unified-page-wrapper">
      <div className="unified-page-header">
        <h3>药膳方详情</h3>
      </div>
      <div className="unified-page-content-scrollable error">
        {error}
      </div>
    </div>
  );
  if (!recipe) return (
    <div className="unified-page-wrapper">
      <div className="unified-page-header">
        <h3>药膳方详情</h3>
      </div>
      <div className="unified-page-content-scrollable">
        没有找到药膳方详情。
      </div>
    </div>
  );

  const imageFile = getImageFileName(recipe.title);
  const heroImage = imageFile ? `/recipe_images/${imageFile}` : '/nomal.png';
  const categorySummary = recipe.categories.length > 0 ? recipe.categories.join(' · ') : '经典滋补药膳方案';
  const leadText = extractLeadText(recipe.content);
  const [primaryCategory, ...otherCategories] = recipe.categories;
  const primaryFocus = primaryCategory ?? '调理体质';
  const effectText = otherCategories.length > 0 ? otherCategories.join(' · ') : '滋补养生 · 平衡体质';

  return (
    <motion.div
      className="unified-page-wrapper recipe-detail-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="unified-page-header detail-page-header">
        <div className="detail-heading">
          <span className="detail-breadcrumb">药膳食疗 · 精选方</span>
          <h3>{recipe.title}</h3>
          {leadText && <p className="detail-lead">{leadText}</p>}
        </div>
  <Link to="/app/dietary-recipes" className="detail-back-link">
          返回列表
        </Link>
      </div>

      <div className="unified-page-content-scrollable recipe-detail-content">
        <section className="recipe-hero-card">
          <div className="recipe-hero-media">
            <img
              src={heroImage}
              alt={recipe.title}
              loading="lazy"
              onError={(event) => {
                const target = event.target as HTMLImageElement;
                if (target.dataset.fallbackApplied) {
                  target.style.display = 'none';
                  return;
                }
                target.dataset.fallbackApplied = 'true';
                target.src = '/nomal.png';
              }}
            />
          </div>
          <div className="recipe-hero-body">
            <div className="recipe-hero-tags">
              {recipe.categories.map((cat) => (
                <span key={cat} style={{ background: getTagColor(cat) }}>{cat}</span>
              ))}
            </div>
            <p className="recipe-hero-summary">{categorySummary}</p>
            <div className="recipe-hero-meta">
              <div>
                <span className="meta-label">编号</span>
                <span className="meta-value">#{recipe.id}</span>
              </div>
              <div>
                <span className="meta-label">适用人群</span>
                <span className="meta-value">{primaryFocus}</span>
              </div>
              <div>
                <span className="meta-label">功效</span>
                <span className="meta-value">{effectText}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="recipe-content-card">
          <ReactMarkdown rehypePlugins={[rehypeSanitize]} remarkPlugins={[remarkGfm]}>
            {recipe.content}
          </ReactMarkdown>
        </section>

        <div className="recipe-detail-actions">
          <Link to="/app/dietary-recipes" className="detail-primary-button">
            返回药膳方列表
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default DietaryRecipeDetail;
